import { isValidEmail } from '@theme/email-validation';

/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths, and validates the email
 * address on the client before the form is submitted.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;
    /** @type {HTMLInputElement | null} */
    this.input = null;
    /** @type {HTMLFormElement | null} */
    this.form = null;
    /** @type {HTMLElement | null} */
    this.clientError = null;
    /** @type {HTMLElement | null} */
    this.clientErrorText = null;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.email-signup__button--integrated');
    this.input = this.querySelector('.email-signup__input');
    this.inputGroup = this.querySelector('.email-signup__input-group');
    this.form = this.querySelector('form');
    this.clientError = this.querySelector('[id^="Email-signup__client-error-"]');
    this.clientErrorText = this.querySelector('.email-signup__client-error-text');

    if (this.form && this.input && this.clientError && this.clientErrorText) {
      this.form.addEventListener('submit', this.handleSubmit);
      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('blur', this.handleBlur);

      // Only turn off native validation once this component has taken over, so the form
      // still validates natively if it is submitted before the script loads.
      this.form.noValidate = true;
    }

    const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit);
      this.form.noValidate = false;
    }
    if (this.input) {
      this.input.removeEventListener('input', this.handleInput);
      this.input.removeEventListener('blur', this.handleBlur);
    }
  }

  /**
   * @returns {'required' | 'invalid' | null} The validation error, or null when the value is valid.
   */
  getError() {
    const value = this.input ? this.input.value.trim() : '';

    if (value === '') return 'required';
    return isValidEmail(value) ? null : 'invalid';
  }

  /**
   * @param {'required' | 'invalid' | null} error
   */
  showError(error) {
    if (!this.input || !this.clientError || !this.clientErrorText) return;

    const serverErrorId = this.input.getAttribute('aria-describedby');

    if (error) {
      this.clientErrorText.textContent =
        (error === 'required' ? this.dataset.errorRequired : this.dataset.errorInvalid) || '';
      this.clientError.removeAttribute('hidden');
      this.input.setAttribute('aria-invalid', 'true');
      this.input.setAttribute('aria-describedby', this.clientError.id);
    } else {
      this.clientErrorText.textContent = '';
      this.clientError.setAttribute('hidden', '');
      this.input.removeAttribute('aria-invalid');
      if (serverErrorId === this.clientError.id) this.input.removeAttribute('aria-describedby');
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    if (!this.input) return;

    const error = this.getError();
    this.input.value = this.input.value.trim();

    if (error) {
      event.preventDefault();
      this.showError(error);
      this.input.focus();
    }
  }

  /** Clears the error as soon as the value becomes valid; never shows one while typing. */
  handleInput() {
    if (this.clientError && !this.clientError.hasAttribute('hidden') && !this.getError()) {
      this.showError(null);
    }
  }

  /** Validates when leaving a non-empty field. */
  handleBlur() {
    if (this.input && this.input.value.trim() !== '') this.showError(this.getError());
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}
